import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidPercentageDecimal,
  useGetBeefManureStorageTypes,
  useGetFootprintingBeefManureStorage,
  useGetFootprintingBeefPrimaryDataById,
  useKeyedFormList,
  useMutateFootprintingBeefManureStorage,
  useMutateFootprintingBeefPrimaryData,
  useTranslation,
} from "utils";

import { UploadableBeefManureStorage } from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  Option,
  PageSpin,
  ProgressBarCardWithContent,
  Select,
  Typography,
} from "components";

type BeefManureStorageFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const manureStoragePlaceholder: UploadableBeefManureStorage = {
  id: undefined,
  manureStorageId: "",
  amountStoredPct: null,
};

const BeefManureStorageForm = ({ primaryDataId, readOnly, farmId, year }: BeefManureStorageFormProps) => {
  const { t } = useTranslation("footprintingBeef");
  const formRef = useRef<FormInstance>(null);

  const [amountStoredGasTankPct, setAmountStoredGasTankPct] = useState<number | null>(null);

  const { data: manureStorageData } = useGetFootprintingBeefManureStorage(primaryDataId ?? "");
  const { data: beefManureStorageQuery, isLoading } = useGetBeefManureStorageTypes();
  const { data: beefPrimaryData, isSuccess } = useGetFootprintingBeefPrimaryDataById(primaryDataId ?? "");
  const keyedManureStorage = useKeyedFormList<UploadableBeefManureStorage>(formRef, { isReady: !isLoading });

  const manureStoragePayloadRef = useRef(keyedManureStorage.payload);

  useEffect(() => {
    manureStoragePayloadRef.current = keyedManureStorage.payload;
  }, [keyedManureStorage.payload]);

  const { mutate: mutateBeefManureStorage } = useMutateFootprintingBeefManureStorage(primaryDataId ?? "", farmId, year);
  const { mutate: mutateBeefPrimaryData } = useMutateFootprintingBeefPrimaryData(
    farmId,
    year,
    { farmId, year, amountStoredGasTankPct, isSubmitted: false },
    primaryDataId
  );

  const selectGroupName = "footprinting-beef-manure-storage-select";

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateBeefManureStorage(manureStoragePayloadRef.current.filter((m) => !!m.manureStorageId));
        mutateBeefPrimaryData({});
      }
    },
    [isLoading, mutateBeefManureStorage, mutateBeefPrimaryData, readOnly]
  );

  useEffect(() => {
    if (manureStorageData) {
      keyedManureStorage.actions.hydrate(
        manureStorageData.length
          ? manureStorageData.map((m) => ({
              id: m.id,
              manureStorageId: m.manureStorageId ?? "",
              amountStoredPct: m.amountStoredPct ?? null,
            }))
          : [manureStoragePlaceholder]
      );
    }
  }, [manureStorageData, keyedManureStorage.actions]);

  useEffect(() => {
    if (beefPrimaryData && isSuccess) {
      setAmountStoredGasTankPct(beefPrimaryData.amountStoredGasTankPct ?? null);

      formRef.current?.setFieldsValue({
        amountStoredGasTankPct: formatFractionInput(beefPrimaryData.amountStoredGasTankPct ?? null),
      });

      if (beefPrimaryData.amountStoredGasTankPct != null) {
        keyedManureStorage.actions.requestRehydrationValidation();
      }
    }
  }, [beefPrimaryData, isSuccess, keyedManureStorage.actions]);

  const totalStoragePercentage = useMemo(
    () => keyedManureStorage.entries.reduce((total, storage) => total + (formatFractionInput(storage.amountStoredPct) || 0), 0),
    [keyedManureStorage.entries]
  );

  const hasLoadedManureStorage = keyedManureStorage.entries.length > 0 && manureStorageData !== undefined;

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Form ref={formRef} preserve={false}>
      <Flex vertical gap={16}>
        <Card border dataTestId="footprinting-beef-manure-storage-form" className="padding-200">
          <Flex vertical gap={16}>
            <Flex vertical>
              <Typography variant="h3">{t("beefManureStorageForm.header")}</Typography>
              <Typography variant="paragraph-m" color="grey-600" className="margin-top-25">
                {t("beefManureStorageForm.description")}
              </Typography>
            </Flex>

            {hasLoadedManureStorage ? (
              <ProgressBarCardWithContent
                currentValue={totalStoragePercentage}
                title={t("beefManureStorageForm.progressBar.title")}
                errorMessage={t("beefManureStorageForm.progressBar.errorMessage")}
                successMessage={t("beefManureStorageForm.progressBar.successMessage")}
                size={8}
              />
            ) : null}

            {keyedManureStorage.entries.map((manure, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;

              return (
                <Form.Item key={manure.clientKey}>
                  <Flex vertical gap={16}>
                    <Flex justify="space-between" align="center" vertical={false}>
                      <Typography variant="h5" style={{ flex: 1 }}>
                        {t("beefManureStorageForm.storageNumber", { count: index + 1 })}
                      </Typography>
                      <IconButton
                        type="tertiary"
                        disabled={readOnly}
                        dataTestId={`delete-beef-manure-storage-${index}`}
                        size="small"
                        iconName="tabler/trash"
                        onClick={() => keyedManureStorage.actions.remove(manure.clientKey)}
                      />
                    </Flex>
                    <Select
                      showSearch={true}
                      disabled={readOnly}
                      name={`${selectGroupName}-${manure.clientKey}`}
                      dataTestId={selectGroupWithIndex}
                      label={t("beefManureStorageForm.question.manureStorageTypeQuestionInput")}
                      rules={[{ required: true, message: "" }]}
                      initialValue={beefManureStorageQuery?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                      value={beefManureStorageQuery?.find((m) => m.id === manure.manureStorageId)?.name || ""}
                      onChange={(v) => {
                        const matchingPractice = beefManureStorageQuery?.find((m) => m.name === v);
                        keyedManureStorage.actions.update(manure.clientKey, { manureStorageId: matchingPractice ? matchingPractice.id : "" });
                      }}
                      dropdownStyle={{ zIndex: 10010 }}
                    >
                      {(beefManureStorageQuery ?? []).map((storageType) => {
                        const isAlreadyUsed = keyedManureStorage.entries.some(
                          (m) => m.manureStorageId === storageType.id && m.manureStorageId !== manure.manureStorageId
                        );
                        return (
                          <Option
                            disabled={readOnly || isAlreadyUsed}
                            key={`${selectGroupWithIndex}-${storageType.id}`}
                            data-testid={`${selectGroupWithIndex}-${storageType.id}`}
                            value={storageType.name}
                          >
                            {storageType.name}
                          </Option>
                        );
                      })}
                    </Select>

                    <InputNumber
                      key={`amountStoredPct-${manure.clientKey}-${manure.manureStorageId}`}
                      disabled={readOnly}
                      name={`amountStoredPct-${manure.clientKey}`}
                      dataTestId="footprinting-beef-manure-storage-pct-input"
                      label={t("beefManureStorageForm.question.manureStoragePctQuestionInput")}
                      suffix="%"
                      value={formatFractionInput(manure.amountStoredPct)}
                      initialValue={formatFractionInput(manure.amountStoredPct)}
                      onChange={(v) => keyedManureStorage.actions.update(manure.clientKey, { amountStoredPct: formatPercentageInput(v) })}
                      validateFirst
                      rules={[
                        { required: true, message: t("beefManureStorageForm.error.validPercentage") },
                        { type: "number" },
                        {
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("beefManureStorageForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Flex>
                </Form.Item>
              );
            })}

            <Button
              type="text"
              dataTestId="add-beef-manure-storage-button"
              disabled={readOnly}
              onClick={() => keyedManureStorage.actions.add(manureStoragePlaceholder)}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("beefManureStorageForm.add")}
            </Button>
          </Flex>
        </Card>

        <Card border className="padding-200">
          <Flex vertical gap={16}>
            <Flex vertical gap={8}>
              <Typography variant="h4">{t("beefManureStorageForm.gasTightStorageTitle")}</Typography>
              <Typography variant="paragraph-m" color="grey-600">
                {t("beefManureStorageForm.gasTightStorageSubtitle")}
              </Typography>
            </Flex>
            <InputNumber
              disabled={readOnly}
              name="amountStoredGasTankPct"
              dataTestId="footprinting-beef-manure-gas-tight-storage-input"
              label={t("beefManureStorageForm.question.gasTightStorageTypeQuestionInput")}
              suffix="%"
              value={formatFractionInput(amountStoredGasTankPct)}
              initialValue={formatFractionInput(amountStoredGasTankPct)}
              onChange={(v) => setAmountStoredGasTankPct(formatPercentageInput(v))}
              validateFirst
              rules={[
                { required: true, message: t("beefManureStorageForm.error.validPercentage") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                      return Promise.reject(new Error(t("beefManureStorageForm.error.validPercentage")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Flex>
        </Card>
      </Flex>
    </Form>
  );
};

BeefManureStorageForm.Key = "beef.manureStorage";
export { BeefManureStorageForm };
